import { useContext } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../../library/Core/GlobalContext';

const FLAG_FIELDS = [
    { key: 'fortune_five_hundred', label: 'Fortune 500' },
    { key: 'startup', label: 'Startup' },
    { key: 'unicorn_status', label: 'Unicorn Status' },
    { key: 'mnc_status', label: 'MNC Status' },
    { key: 'listed_status', label: 'Listed' },
    { key: 'is_defence', label: 'Defence Company' },
    { key: 'is_prestigious', label: 'Prestigious Company' },
    { key: 'is_registered_company', label: 'Registered Company' },
    { key: 'is_indian_government', label: 'Indian Government' },
    { key: 'is_international_government', label: 'International Government' },
    { key: 'is_international_body', label: 'International Body (UNO, World Bank, IMF etc)' },
    { key: 'is_law_firm', label: 'Law Firm' },
    { key: 'is_courts_tribunals', label: 'Courts and Tribunals' },
    { key: 'is_educational_institute', label: 'Educational Institute' },
    { key: 'is_healthcare_organization', label: 'Hospital or Healthcare Organization' },
    { key: 'is_ngo', label: 'NGO' },
];

const OPERATIONAL_STATUS_DOMAIN = 'COMPANY_OPERATIONAL_STATUS';
const EMPLOYEE_COUNT_DOMAIN = 'COMPANY_EMPLOYEE_COUNT';
const COMPANY_AGE_DOMAIN = 'COMPANY_AGE';

const CompanyView = () => {
    const loaderData = useLoaderData() as any;
    const navigate = useNavigate();
    const company = loaderData?.company ?? {};
    const { globalState } = useContext(GlobalContext);
    const domainLabel = (category: string, code: unknown) =>
        ((globalState as any)?.domain?.get?.(category) ?? []).find((d: any) => String(d.code) === String(code))?.value ||
        (code as string) ||
        '';
    const operationalStatusLabel = domainLabel(OPERATIONAL_STATUS_DOMAIN, company.operational_status);
    const employeeCountLabel = domainLabel(EMPLOYEE_COUNT_DOMAIN, company.company_size);

    const companyAgeLabel = (yearValue: unknown): string | undefined => {
        if (yearValue == null || yearValue === '') return undefined;
        const year = Number(yearValue);
        const current = new Date().getFullYear();
        if (!Number.isInteger(year) || year < 1900 || year > current) return String(yearValue);
        const age = current - year;
        const bandCode = age < 2 ? '<2 years' : age <= 5 ? '2-5 Years' : age <= 10 ? '6-10 years' : '10+ years';
        const band = domainLabel(COMPANY_AGE_DOMAIN, bandCode);
        return `${year} (${age === 1 ? '1 year' : `${age} years`} · ${band})`;
    };

    const InfoRow = ({ label, value }: { label: string; value?: string }) => (
        <div className="col-md-6 mb-3">
            <div className="font-12 text-muted mb-1">{label}</div>
            <div className="font-14 font-500">{value || <span className="text-muted">—</span>}</div>
        </div>
    );

    return (
        <div className="main flex-1">
            <section className="updatebasicinformation">
                <div className="container max-1140 px-lg-0 overflow-hidden">
                    <div className="row">
                        <div className="col-md-12 mb-4">
                            <div className="white-block white-block-notopborderradius p-0 h-100">

                                {/* Header */}
                                <div className="border-bottom border-2 custom-border-gray p-3 p-sm-4 pb-3">
                                    <div className="d-flex flex-wrap justify-content-between align-items-center mx-0 mx-sm-1">
                                        <div className="d-flex align-items-center gap-3">
                                            {company.logo && (
                                                <img
                                                    src={company.logo}
                                                    alt={company.name}
                                                    style={{
                                                        width: 56,
                                                        height: 56,
                                                        objectFit: 'contain',
                                                        border: '1px solid #eee',
                                                        borderRadius: 8,
                                                        padding: 4,
                                                        background: '#fff',
                                                    }}
                                                />
                                            )}
                                            <h1 className="font-26 font-600 my-0 py-2">
                                                {company.name || 'Company Details'}
                                            </h1>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-green btn-sm"
                                                onClick={() =>
                                                    navigate(`/company/${loaderData?.routeInfo?.id}/basic-info`)
                                                }>
                                                <span>Edit Company</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/company/listing')}>
                                                ← Back to Listing
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 p-md-5">

                                    {/* Basic Details */}
                                    <div className="mb-5">
                                        <h5 className="font-18 font-600 mb-4 pb-2 border-bottom">
                                            Basic Information
                                        </h5>
                                        <div className="row">
                                            <InfoRow label="Company Name" value={company.name} />
                                            <InfoRow label="Website" value={company.website} />
                                            <InfoRow label="Country" value={company.country} />
                                            <InfoRow
                                                label="Headquarters Country"
                                                value={company.headquarters_country}
                                            />
                                            <InfoRow label="Industry" value={company.industry} />
                                            <InfoRow label="Employee Count" value={employeeCountLabel} />
                                            <InfoRow
                                                label="Establishment Year"
                                                value={companyAgeLabel(company.establishment_year)}
                                            />
                                        </div>
                                    </div>

                                    {/* Company Attributes */}
                                    <div className="mb-5">
                                        <h5 className="font-18 font-600 mb-4 pb-2 border-bottom">
                                            Company Attributes
                                        </h5>
                                        <div className="row g-3">
                                            {FLAG_FIELDS.map(({ key, label }) => {
                                                const isYes = Number(company[key]) === 1;
                                                return (
                                                    <div className="col-md-3 col-sm-6" key={key}>
                                                        <div
                                                            className="p-3 rounded d-flex align-items-center justify-content-between"
                                                            style={{
                                                                border: `1px solid ${isYes ? '#28a74540' : '#e8e8e8'}`,
                                                                background: isYes ? '#f0fff4' : '#fafafa',
                                                            }}>
                                                            <span className="font-13 font-500">{label}</span>
                                                            <span
                                                                className={`badge ${isYes ? 'bg-success' : 'bg-secondary'}`}
                                                                style={{ fontSize: 12 }}>
                                                                {isYes ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Balance Sheets */}
                                    <div>
                                        <h5 className="font-18 font-600 mb-4 pb-2 border-bottom">
                                            Balance Sheets
                                        </h5>
                                        {company.annual_revenue || company.balance_sheets || company.operational_status ? (
                                            <div className="table-responsive">
                                                <table className="table table-bordered">
                                                    <thead>
                                                        <tr>
                                                            <th>Operational Status</th>
                                                            <th>Annual Revenue</th>
                                                            <th style={{ width: 110 }}>Profitable</th>
                                                            <th>Document</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td className="font-14 font-500">
                                                                {operationalStatusLabel || '—'}
                                                            </td>
                                                            <td className="font-14 font-500">{company.annual_revenue || '—'}</td>
                                                            <td>
                                                                <span className={`badge ${company.profitable === 'Yes' ? 'bg-success' : 'bg-secondary'}`}>
                                                                    {company.profitable || 'No'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {company.balance_sheets ? (
                                                                    <a href={company.balance_sheets} target="_blank" rel="noopener noreferrer" className="text-sky-blue font-13">
                                                                        📊 View Balance Sheet
                                                                    </a>
                                                                ) : '—'}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div
                                                className="text-center py-4 text-muted font-14"
                                                style={{ border: '1px dashed #ccc', borderRadius: 8 }}>
                                                No balance sheets available.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CompanyView;
